(function () {
  'use strict';

  const ESCAPE_REGEX = /([ #;&,.+*~\':"!^$[\]()=>|\/@])/g;

  function escapeValue(value) {
    return value.replace(ESCAPE_REGEX, '\\$1');
  }

  // ---------------------------------------------------------------------------
  // MainProduct
  //
  // Intercepts variant radio changes in the capture phase (before the theme's
  // bubble handler). When the selected combination has no matching variant,
  // finds the best available variant that keeps the newly-clicked option value
  // and silently updates the other inputs so the theme reads a valid variant.
  // ---------------------------------------------------------------------------

  class MainProduct {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.form = container.querySelector('#AddToCartForm-' + this.sectionId);

      if (!this.form) return;

      const variantsEl = container.querySelector('#VariantsJson-' + this.sectionId);
      if (!variantsEl) return;

      this.variants = JSON.parse(variantsEl.textContent.trim());
      this.inputSelector = '.variant__input-' + this.sectionId;

      this.form.addEventListener('change', this._onOptionChange.bind(this), true);
    }

    // Returns e.g. { option1: 'Black', option2: 'S', option3: 'Youth' }
    _getSelectedOptions() {
      const options = {};
      this.form.querySelectorAll(this.inputSelector + ':checked').forEach((input) => {
        options[input.dataset.index] = input.value;
      });
      return options;
    }

    _findExactVariant(options) {
      return this.variants.find((v) =>
        Object.keys(options).every((k) => v[k] === options[k])
      ) || null;
    }

    // Find the closest variant that keeps the newly-clicked option value,
    // preferring candidates that preserve other current selections, then available ones
    _findBestVariant(changedIndex, changedValue, currentOptions) {
      const otherOptions = Object.fromEntries(
        Object.entries(currentOptions).filter(([k]) => k !== changedIndex)
      );

      const candidates = this.variants.filter((v) => v[changedIndex] === changedValue);
      if (!candidates.length) return null;

      return candidates.slice().sort((a, b) => {
        const aScore = Object.keys(otherOptions).filter((k) => a[k] === otherOptions[k]).length;
        const bScore = Object.keys(otherOptions).filter((k) => b[k] === otherOptions[k]).length;
        if (aScore !== bScore) return bScore - aScore;
        if (a.available !== b.available) return b.available ? 1 : -1;
        return 0;
      })[0];
    }

    // Check the radio inputs to match the given variant without firing change events
    _syncInputsToVariant(variant) {
      ['option1', 'option2', 'option3'].forEach((optIndex) => {
        const value = variant[optIndex];
        if (!value) return;

        const wrapper = this.form.querySelector(
          `.variant-input[data-index="${optIndex}"][data-value="${escapeValue(value)}"]`
        );
        if (!wrapper) return;

        const input = wrapper.querySelector('input');
        if (input && !input.checked) input.checked = true;
      });
    }

    _onOptionChange(evt) {
      if (!evt.target.matches(this.inputSelector)) return;

      const changedIndex = evt.target.dataset.index;
      const changedValue = evt.target.value;
      const selectedOptions = this._getSelectedOptions();

      if (this._findExactVariant(selectedOptions)) return;

      const best = this._findBestVariant(changedIndex, changedValue, selectedOptions);
      if (best) this._syncInputsToVariant(best);
    }
  }

  // ---------------------------------------------------------------------------
  // AvailabilityFix
  //
  // The theme's setAvailability only enables option values reachable from the
  // current variant by changing a single option. For products with an Age option
  // this incorrectly disables Youth/Mens on load because no variant shares both
  // Color+Size with a different age.
  //
  // Runs a post-correction pass after the theme's handler (via setTimeout):
  //   - Age group:    enable if ANY variant with that age exists
  //   - Other groups: enable if any variant exists for that value + selected age
  // Only ever re-enables; the theme still owns the disabling logic.
  // ---------------------------------------------------------------------------

  class AvailabilityFix {
    constructor(container, variants, inputSelector) {
      this.container = container;
      this.variants = variants;
      this.inputSelector = inputSelector;

      const form = container.querySelector('#AddToCartForm-' + container.dataset.sectionId);
      if (!form) return;
      this.form = form;

      this.ageGroup = form.querySelector('.variant-input-wrap[data-handle="age"]');
      if (!this.ageGroup) return;
      this.ageIndex = this.ageGroup.dataset.index;

      container.addEventListener('variantChange', () => {
        setTimeout(() => this._correct(), 0);
      });

      setTimeout(() => this._correct(), 0);
    }

    _selectedAge() {
      const checked = this.ageGroup.querySelector('input:checked');
      return checked ? checked.value : null;
    }

    _correct() {
      const selectedAge = this._selectedAge();

      this.form.querySelectorAll('.variant-input-wrap').forEach((group) => {
        const optIndex = group.dataset.index;
        const isAgeGroup = optIndex === this.ageIndex;

        group.querySelectorAll('.variant-input').forEach((wrapper) => {
          const input = wrapper.querySelector('input');
          const label = wrapper.querySelector('label');

          if (!input.classList.contains('disabled')) return;

          const value = wrapper.dataset.value;
          let shouldEnable;

          if (isAgeGroup) {
            shouldEnable = this.variants.some((v) => v[optIndex] === value);
          } else {
            shouldEnable = this.variants.some(
              (v) => v[optIndex] === value && v[this.ageIndex] === selectedAge
            );
          }

          if (shouldEnable) {
            input.classList.remove('disabled');
            label.classList.remove('disabled');
          }
        });
      });
    }
  }

  // ---------------------------------------------------------------------------
  // AgeLabelUpdater
  //
  // Reads the age_labels theme setting (passed via data-age-labels on the
  // section container) and updates the #AgeLabelText span inside the Age
  // variant label whenever an Age radio is changed.
  //
  // Setting format: "kids: 3-7 Years Old;;youth: 8-14 Years Old;;"
  // Split on ;; → split each on first : → { kids: '3-7 Years Old', ... }
  // ---------------------------------------------------------------------------

  class AgeLabelUpdater {
    constructor(container, inputSelector) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.inputSelector = inputSelector;

      const raw = container.dataset.ageLabels || '';
      this.labelMap = this._parse(raw);
      if (!Object.keys(this.labelMap).length) return;

      const form = container.querySelector('#AddToCartForm-' + this.sectionId);
      if (!form) return;
      this.form = form;

      this.ageGroup = form.querySelector('.variant-input-wrap[data-handle="age"]');
      if (!this.ageGroup) return;

      this.span = container.querySelector('#AgeLabelText-' + this.sectionId);
      if (!this.span) return;

      this.ageGroup.addEventListener('change', () => this._update());
    }

    _parse(raw) {
      const map = {};
      raw.split(';;').forEach((entry) => {
        const trimmed = entry.trim();
        if (!trimmed) return;
        const colonIdx = trimmed.indexOf(':');
        if (colonIdx === -1) return;
        const key = trimmed.slice(0, colonIdx).trim().toLowerCase();
        const value = trimmed.slice(colonIdx + 1).trim();
        if (key && value) map[key] = value;
      });
      return map;
    }

    _update() {
      const checked = this.ageGroup.querySelector('input:checked');
      const key = checked ? checked.value.toLowerCase() : '';
      const label = this.labelMap[key] || '';
      this.span.textContent = label;
    }
  }

  function init() {
    document.querySelectorAll('[data-section-type="product"]').forEach((container) => {
      const instance = new MainProduct(container);
      new AvailabilityFix(container, instance.variants || [], instance.inputSelector || '');
      new AgeLabelUpdater(container, instance.inputSelector || '');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
